import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, CheckCircle2, Filter, RotateCw, Edit3, Save, X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "bg-emerald-100 text-emerald-800",
  unchanged: "bg-zinc-100 text-zinc-700",
  modified: "bg-blue-100 text-blue-800",
  possible_duplicate: "bg-amber-100 text-amber-800",
  unrecognized: "bg-red-100 text-red-700",
  needs_review: "bg-purple-100 text-purple-800",
  conflict: "bg-red-200 text-red-900",
};
const STATUS_LABEL = {
  new: "Nuovo", unchanged: "Invariato", modified: "Modificato",
  possible_duplicate: "Duplicato?", unrecognized: "Non ricon.", needs_review: "Da verificare", conflict: "Conflitto",
};

const SIM_MODES = [
  { v: "prudente", l: "Prudente", d: "Meno match: preferisce creare nuove gare in caso di dubbio" },
  { v: "standard", l: "Standard", d: "Soglie bilanciate (raccomandato)" },
  { v: "aggressivo", l: "Aggressivo", d: "Più match: unisce gare simili anche con dati diversi" },
];

// step: upload | review | wizard1 | wizard2 | wizard3 | done
export default function FISImportV2Dialog({ onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [season, setSeason] = useState("2026-2027");
  const [simMode, setSimMode] = useState("standard");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [events, setEvents] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [editing, setEditing] = useState({}); // eid -> edited fields
  const [fStatus, setFStatus] = useState("");
  const [fMonth, setFMonth] = useState("");
  const [fWeapon, setFWeapon] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [savedSessions, setSavedSessions] = useState([]);
  const [confirmResult, setConfirmResult] = useState(null);

  const loadSessions = async () => {
    try { const { data } = await api.get("/import/fis-v2/sessions"); setSavedSessions(data); } catch (_) {}
  };
  useEffect(() => { if (open) loadSessions(); }, [open]);

  const upload = async () => {
    if (!file) return toast.error("Seleziona un file PDF");
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/import/fis-v2/upload?season=${encodeURIComponent(season)}&similarity_mode=${simMode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSessionId(data.session_id);
      await loadSession(data.session_id);
      setStep("review");
      if (data.reused) toast.info("Sessione esistente riaperta");
      else toast.success(`${data.stats.events} eventi rilevati in ${data.stats.parsed_pages}/${data.stats.total_pages} pagine`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore parsing"); }
    finally { setLoading(false); }
  };

  const loadSession = async (sid) => {
    const { data } = await api.get(`/import/fis-v2/sessions/${sid}`);
    setSession(data.session); setEvents(data.events);
    const dec = {};
    data.events.forEach((e) => { dec[e.id] = e.proposed_action; });
    setDecisions(dec); setEditing({});
  };

  const resumeSession = async (sid) => { setSessionId(sid); await loadSession(sid); setStep("review"); };
  const setDecision = (eid, action) => setDecisions((d) => ({ ...d, [eid]: action }));

  const startEdit = (e) => setEditing((s) => ({ ...s, [e.id]: {
    start_date: e.start_date || "", end_date: e.end_date || "",
    event_name_raw: e.event_name_raw || "", location_normalized: e.location_normalized || "",
    weapon_normalized: e.weapon_normalized || "", category: e.category || "",
  }}));
  const cancelEdit = (eid) => setEditing((s) => { const n = { ...s }; delete n[eid]; return n; });
  const saveEdit = async (eid) => {
    try {
      await api.patch(`/import/fis-v2/staged/${eid}`, editing[eid]);
      toast.success("Modificato");
      await loadSession(sessionId);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const filtered = useMemo(() => events.filter((e) => {
    if (fStatus && e.change_status !== fStatus) return false;
    if (fMonth && e.source_month !== parseInt(fMonth)) return false;
    if (fWeapon && e.weapon_normalized !== fWeapon) return false;
    if (fSearch && !(e.event_name_raw || "").toLowerCase().includes(fSearch.toLowerCase())) return false;
    return true;
  }), [events, fStatus, fMonth, fWeapon, fSearch]);

  // Wizard summary counters (based on decisions, not on server counters)
  const wizardSummary = useMemo(() => {
    let newC = 0, upd = 0, ign = 0, conf = 0;
    events.forEach((e) => {
      const a = decisions[e.id] || "ignore";
      if (a === "import_new" || a === "force_new") newC++;
      else if (a === "update_existing" || a === "merge") upd++;
      else if (a === "ignore") ign++;
      if (e.change_status === "conflict") conf++;
    });
    return { total: events.length, newC, upd, ign, conf };
  }, [events, decisions]);

  const counters = session?.counters || {};
  const applyBulk = (action) => {
    const map = {...decisions};
    filtered.forEach((e) => { map[e.id] = action; });
    setDecisions(map);
    toast.success(`${filtered.length} righe → ${action}`);
  };

  const confirm = async () => {
    if (!sessionId) return;
    const decs = events.map((e) => ({ staged_id: e.id, action: decisions[e.id] || "ignore", matched_event_id: e.matched_event_id }));
    setLoading(true);
    try {
      const { data } = await api.post(`/import/fis-v2/sessions/${sessionId}/confirm`, { session_id: sessionId, decisions: decs });
      setConfirmResult(data);
      setStep("done");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); setStep("wizard2"); }
    finally { setLoading(false); }
  };

  const reset = () => {
    setStep("upload"); setFile(null); setSessionId(null); setSession(null);
    setEvents([]); setDecisions({}); setEditing({}); setFStatus(""); setFMonth(""); setFWeapon(""); setFSearch(""); setConfirmResult(null);
  };

  const WizardStep = ({ n, label, active, done }) => (
    <div className={`flex items-center gap-2 ${active ? "text-black" : done ? "text-emerald-600" : "text-zinc-400"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono ${active ? "bg-black text-white" : done ? "bg-emerald-500 text-white" : "bg-zinc-200 text-zinc-500"}`}>
        {done ? "✓" : n}
      </div>
      <span className="text-xs font-mono uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>{trigger || <Button variant="outline" data-testid="fis-v2-btn"><FileText className="w-4 h-4 mr-2" />Importa calendario FIS</Button>}</div>
      <DialogContent className="max-w-7xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importa calendario FIS</DialogTitle>
          <DialogDescription>Carica il PDF del calendario federale, rivedi le gare rilevate e conferma in 4 passi.</DialogDescription>
        </DialogHeader>

        {/* Upload step */}
        {step === "upload" && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Stagione sportiva</Label>
                <Input data-testid="fis-season" value={season} onChange={(e) => setSeason(e.target.value)} placeholder="2026-2027" />
              </div>
              <div>
                <Label>Modalità matching</Label>
                <Select value={simMode} onValueChange={setSimMode}>
                  <SelectTrigger data-testid="fis-simmode"><SelectValue /></SelectTrigger>
                  <SelectContent>{SIM_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                </Select>
                <div className="text-[10px] text-zinc-500 mt-1">{SIM_MODES.find((m) => m.v === simMode)?.d}</div>
              </div>
            </div>
            <div>
              <Label>File PDF calendario FIS</Label>
              <input data-testid="fis-file" type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="block w-full text-sm mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-black file:text-white hover:file:bg-zinc-800" />
              {file && <div className="text-xs text-zinc-500 mt-1 font-mono">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
            </div>
            <Button data-testid="fis-upload" onClick={upload} disabled={!file || loading} className="w-full h-12 bg-black hover:bg-zinc-800">
              {loading ? "Parsing…" : <><Upload className="w-4 h-4 mr-2" />Analizza PDF</>}
            </Button>
            {savedSessions.length > 0 && (
              <div className="pt-4 border-t border-zinc-200">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono mb-2">Sessioni salvate</div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {savedSessions.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 bg-zinc-50 rounded p-2 border border-zinc-200">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{s.source_file_name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{s.uploaded_at?.slice(0,10)} · {s.detected_events} eventi · <span className={`px-1 rounded ${s.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{s.status}</span></div>
                      </div>
                      {s.status !== "confirmed" && <Button size="sm" variant="outline" onClick={() => resumeSession(s.id)} data-testid={`resume-${s.id}`}><RotateCw className="w-3 h-3 mr-1" />Riprendi</Button>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Review + inline edit step */}
        {step === "review" && session && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
              <div className="bg-zinc-50 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Pagine</div><div className="font-mono text-lg font-bold">{session.parsed_pages}/{session.total_pages}</div></div>
              <div className="bg-zinc-50 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Eventi</div><div className="font-mono text-lg font-bold">{session.detected_events}</div></div>
              <div className="bg-emerald-50 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-emerald-700 font-mono">Nuovi</div><div className="font-mono text-lg font-bold text-emerald-800">{counters.new || 0}</div></div>
              <div className="bg-blue-50 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-blue-700 font-mono">Modificati</div><div className="font-mono text-lg font-bold text-blue-800">{counters.modified || 0}</div></div>
              <div className="bg-amber-50 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-amber-700 font-mono">Duplicati?</div><div className="font-mono text-lg font-bold text-amber-800">{counters.possible_duplicate || 0}</div></div>
              <div className="bg-purple-50 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-purple-700 font-mono">Da verif.</div><div className="font-mono text-lg font-bold text-purple-800">{counters.needs_review || 0}</div></div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-md p-3">
              <div className="grid md:grid-cols-4 gap-2">
                <Select value={fStatus || "all"} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="f-status" className="h-9"><SelectValue placeholder="Stato" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    {Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fMonth || "all"} onValueChange={(v) => setFMonth(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Mese" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutti i mesi</SelectItem>{["1","2","3","4","5","6","7","8","9","10","11","12"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={fWeapon || "all"} onValueChange={(v) => setFWeapon(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Arma" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="fioretto">Fioretto</SelectItem><SelectItem value="spada">Spada</SelectItem><SelectItem value="sciabola">Sciabola</SelectItem><SelectItem value="6_armi">6 armi</SelectItem></SelectContent>
                </Select>
                <Input placeholder="Cerca nome…" value={fSearch} onChange={(e) => setFSearch(e.target.value)} className="h-9" data-testid="f-search" />
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs text-zinc-500 self-center">Su {filtered.length} righe filtrate:</span>
                <Button size="sm" variant="outline" onClick={() => applyBulk("import_new")}>Importa nuovi</Button>
                <Button size="sm" variant="outline" onClick={() => applyBulk("update_existing")}>Aggiorna esistenti</Button>
                <Button size="sm" variant="outline" onClick={() => applyBulk("ignore")}>Ignora tutti</Button>
                <Button size="sm" variant="outline" onClick={() => setFStatus("needs_review")}>Solo da verificare</Button>
              </div>
            </div>

            <div className="max-h-[50vh] overflow-y-auto bg-white border border-zinc-200 rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-2">Stato</th>
                    <th className="text-left px-2 py-2 w-24">Data inizio</th>
                    <th className="text-left px-2 py-2 w-24">Data fine</th>
                    <th className="text-left px-2 py-2">Nome</th>
                    <th className="text-left px-2 py-2 w-24">Arma</th>
                    <th className="text-left px-2 py-2 w-20">Cat.</th>
                    <th className="text-left px-2 py-2 w-32">Località</th>
                    <th className="text-right px-2 py-2 w-14">Conf.</th>
                    <th className="text-right px-2 py-2 w-12">Pg</th>
                    <th className="text-left px-2 py-2 w-44">Azione / Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => {
                    const ed = editing[e.id];
                    if (ed) {
                      return (
                        <tr key={e.id} className="border-t border-zinc-100 bg-blue-50/30">
                          <td className="px-2 py-1.5"><span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_COLORS[e.change_status]}`}>{STATUS_LABEL[e.change_status]}</span></td>
                          <td className="px-1 py-1"><Input type="date" value={ed.start_date} onChange={(ev) => setEditing((s) => ({...s, [e.id]: {...s[e.id], start_date: ev.target.value}}))} className="h-7 text-xs" /></td>
                          <td className="px-1 py-1"><Input type="date" value={ed.end_date} onChange={(ev) => setEditing((s) => ({...s, [e.id]: {...s[e.id], end_date: ev.target.value}}))} className="h-7 text-xs" /></td>
                          <td className="px-1 py-1"><Input value={ed.event_name_raw} onChange={(ev) => setEditing((s) => ({...s, [e.id]: {...s[e.id], event_name_raw: ev.target.value}}))} className="h-7 text-xs" /></td>
                          <td className="px-1 py-1">
                            <Select value={ed.weapon_normalized || "none"} onValueChange={(v) => setEditing((s) => ({...s, [e.id]: {...s[e.id], weapon_normalized: v === "none" ? "" : v}}))}>
                              <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="fioretto">Fioretto</SelectItem><SelectItem value="spada">Spada</SelectItem><SelectItem value="sciabola">Sciabola</SelectItem><SelectItem value="6_armi">6 armi</SelectItem></SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1"><Input value={ed.category} onChange={(ev) => setEditing((s) => ({...s, [e.id]: {...s[e.id], category: ev.target.value}}))} className="h-7 text-xs" /></td>
                          <td className="px-1 py-1"><Input value={ed.location_normalized} onChange={(ev) => setEditing((s) => ({...s, [e.id]: {...s[e.id], location_normalized: ev.target.value}}))} className="h-7 text-xs" /></td>
                          <td className="px-2 py-1.5 text-right font-mono">{Math.round((e.confidence_score || 0) * 100)}%</td>
                          <td className="px-2 py-1.5 text-right text-zinc-400">{e.page_number}</td>
                          <td className="px-1 py-1">
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => saveEdit(e.id)} className="h-7 px-2 bg-black hover:bg-zinc-800" data-testid={`save-${e.id}`}><Save className="w-3 h-3" /></Button>
                              <Button size="sm" variant="outline" onClick={() => cancelEdit(e.id)} className="h-7 px-2"><X className="w-3 h-3" /></Button>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                        <td className="px-2 py-1.5"><span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_COLORS[e.change_status]}`}>{STATUS_LABEL[e.change_status]}</span></td>
                        <td className="px-2 py-1.5 font-mono">{e.start_date || <span className="text-zinc-400">{e.raw_date || "—"}</span>}</td>
                        <td className="px-2 py-1.5 font-mono">{e.end_date || <span className="text-zinc-400">—</span>}</td>
                        <td className="px-2 py-1.5 max-w-xs truncate" title={e.event_name_raw}>{e.event_name_raw || "—"}</td>
                        <td className="px-2 py-1.5 capitalize">{e.weapon_normalized || <span className="text-zinc-400">—</span>}</td>
                        <td className="px-2 py-1.5">{e.category || <span className="text-zinc-400">—</span>}</td>
                        <td className="px-2 py-1.5 truncate max-w-[8rem]" title={e.location_normalized}>{e.location_normalized || <span className="text-zinc-400">—</span>}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{Math.round((e.confidence_score || 0) * 100)}%</td>
                        <td className="px-2 py-1.5 text-right text-zinc-400">{e.page_number}</td>
                        <td className="px-1 py-1">
                          <div className="flex gap-1 items-center">
                            <Select value={decisions[e.id] || "ignore"} onValueChange={(v) => setDecision(e.id, v)}>
                              <SelectTrigger className="h-7 text-xs flex-1" data-testid={`action-${e.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="import_new">Importa nuova</SelectItem>
                                <SelectItem value="update_existing" disabled={!e.matched_event_id}>Aggiorna esistente</SelectItem>
                                <SelectItem value="merge" disabled={!e.matched_event_id}>Unisci</SelectItem>
                                <SelectItem value="ignore">Ignora</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => startEdit(e)} data-testid={`edit-${e.id}`}><Edit3 className="w-3 h-3" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-zinc-500">Nessun evento con i filtri correnti.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Wizard steps */}
        {["wizard1","wizard2","wizard3"].includes(step) && (
          <div className="space-y-6 py-6">
            <div className="flex items-center justify-between max-w-3xl mx-auto">
              <WizardStep n="1" label="Riepilogo" active={step === "wizard1"} done={["wizard2","wizard3"].includes(step)} />
              <div className="flex-1 h-px bg-zinc-200 mx-2" />
              <WizardStep n="2" label="Dettaglio" active={step === "wizard2"} done={step === "wizard3"} />
              <div className="flex-1 h-px bg-zinc-200 mx-2" />
              <WizardStep n="3" label="Conferma" active={step === "wizard3"} done={false} />
            </div>

            {step === "wizard1" && (
              <div className="text-center py-8">
                <FileText className="w-16 h-16 mx-auto text-[#0052FF] mb-4" />
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono mb-2">STEP 1 · Riepilogo</div>
                <div className="font-mono text-6xl font-bold">{wizardSummary.total}</div>
                <div className="text-lg text-zinc-600 mt-2">eventi trovati nel PDF</div>
                <div className="text-xs text-zinc-500 mt-4 font-mono">{session?.source_file_name} · stagione {session?.season || "—"}</div>
              </div>
            )}

            {step === "wizard2" && (
              <div className="py-4">
                <div className="text-center mb-6">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">STEP 2 · Cosa verrà fatto</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                  <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4 text-center">
                    <div className="font-mono text-4xl font-bold text-emerald-700">{wizardSummary.newC}</div>
                    <div className="text-xs uppercase tracking-wider text-emerald-800 font-mono mt-1">Nuove gare</div>
                  </div>
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                    <div className="font-mono text-4xl font-bold text-blue-700">{wizardSummary.upd}</div>
                    <div className="text-xs uppercase tracking-wider text-blue-800 font-mono mt-1">Aggiornamenti</div>
                  </div>
                  <div className="bg-zinc-50 border-2 border-zinc-200 rounded-lg p-4 text-center">
                    <div className="font-mono text-4xl font-bold text-zinc-600">{wizardSummary.ign}</div>
                    <div className="text-xs uppercase tracking-wider text-zinc-700 font-mono mt-1">Ignorate</div>
                  </div>
                  <div className={`${wizardSummary.conf > 0 ? "bg-red-50 border-red-200" : "bg-zinc-50 border-zinc-200"} border-2 rounded-lg p-4 text-center`}>
                    <div className={`font-mono text-4xl font-bold ${wizardSummary.conf > 0 ? "text-red-700" : "text-zinc-500"}`}>{wizardSummary.conf}</div>
                    <div className={`text-xs uppercase tracking-wider font-mono mt-1 ${wizardSummary.conf > 0 ? "text-red-800" : "text-zinc-600"}`}>Conflitti</div>
                  </div>
                </div>
                {wizardSummary.conf > 0 && (
                  <div className="mt-6 max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                    ⚠️ Ci sono <strong>{wizardSummary.conf} conflitti</strong> con gare che hanno già convocati o dati logistici. Sarà richiesta approvazione manuale prima di modificarli.
                  </div>
                )}
              </div>
            )}

            {step === "wizard3" && (
              <div className="text-center py-8">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono mb-4">STEP 3 · Confermi l'importazione?</div>
                <div className="text-lg mb-6">Verranno create <strong>{wizardSummary.newC} nuove gare</strong>, aggiornate <strong>{wizardSummary.upd}</strong>, ignorate <strong>{wizardSummary.ign}</strong>.</div>
                <div className="text-xs text-zinc-500 max-w-md mx-auto">Nessun dato esistente verrà cancellato. Ogni modifica sarà tracciata nello storico versioni.</div>
              </div>
            )}
          </div>
        )}

        {/* Done step */}
        {step === "done" && confirmResult && (
          <div className="text-center py-10 space-y-4">
            <CheckCircle2 className="w-20 h-20 mx-auto text-emerald-500" />
            <div className="font-heading text-2xl font-bold">Importazione completata</div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-w-2xl mx-auto text-sm">
              <div><div className="font-mono text-3xl font-bold text-emerald-700">{confirmResult.created}</div><div className="text-xs text-zinc-500">create</div></div>
              <div><div className="font-mono text-3xl font-bold text-blue-700">{confirmResult.updated}</div><div className="text-xs text-zinc-500">aggiornate</div></div>
              <div><div className="font-mono text-3xl font-bold text-zinc-500">{confirmResult.ignored}</div><div className="text-xs text-zinc-500">ignorate</div></div>
              {confirmResult.conflicts > 0 && <div><div className="font-mono text-3xl font-bold text-red-600">{confirmResult.conflicts}</div><div className="text-xs text-zinc-500">conflitti</div></div>}
            </div>
            {confirmResult.conflicts > 0 && <div className="text-sm text-amber-700 max-w-md mx-auto">Ci sono {confirmResult.conflicts} conflitti che richiedono approvazione manuale. La sessione resta aperta.</div>}
          </div>
        )}

        <DialogFooter>
          {step === "upload" && <Button variant="outline" onClick={() => setOpen(false)}>Chiudi</Button>}
          {step === "review" && (
            <>
              <Button variant="outline" onClick={reset}>Nuovo upload</Button>
              <Button variant="outline" onClick={() => setOpen(false)}>Salva sessione</Button>
              <Button data-testid="fis-to-wizard" onClick={() => setStep("wizard1")} className="bg-black hover:bg-zinc-800">
                Avvia conferma <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === "wizard1" && (
            <>
              <Button variant="outline" onClick={() => setStep("review")}><ChevronLeft className="w-4 h-4 mr-1" />Torna alla revisione</Button>
              <Button onClick={() => setStep("wizard2")} className="bg-black hover:bg-zinc-800">Prosegui <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </>
          )}
          {step === "wizard2" && (
            <>
              <Button variant="outline" onClick={() => setStep("wizard1")}><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button>
              <Button onClick={() => setStep("wizard3")} className="bg-black hover:bg-zinc-800">Prosegui <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </>
          )}
          {step === "wizard3" && (
            <>
              <Button variant="outline" onClick={() => setStep("review")}><ChevronLeft className="w-4 h-4 mr-1" />Torna alla revisione</Button>
              <Button data-testid="fis-final-confirm" onClick={confirm} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
                {loading ? "Salvataggio…" : <><CheckCircle2 className="w-4 h-4 mr-2" />Sì, importa ora</>}
              </Button>
            </>
          )}
          {step === "done" && (
            <>
              <Button variant="outline" onClick={reset}>Nuovo import</Button>
              <Button onClick={() => { setOpen(false); reset(); onDone?.(); }} className="bg-black hover:bg-zinc-800">Chiudi</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
